import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { BRAND } from "../mock";
import { apiSubmitContact } from "../lib/api";
import { toast } from "sonner";
import SEO from "../components/SEO";

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    const fd = new FormData(form);
    try {
      await apiSubmitContact({
        name: fd.get("name"),
        email: fd.get("email"),
        subject: fd.get("subject") || "",
        message: fd.get("message"),
      });
      toast.success("Thanks. We'll reply within 24 hours.");
      form.reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-20" data-testid="contact-page">
      <SEO
        title="Contact Certified Wellness"
        description="Questions about our research compounds, COAs, or your order? Our team replies within 24 hours. Email support@certifiedwellness.net."
        canonical="/contact"
      />
      <div className="max-w-3xl mb-14">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Get In Touch</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
          Contact <span className="font-serif italic font-normal">{BRAND.name}.</span>
        </h1>
        <p className="text-gray-500 mt-5 text-[17px] font-light leading-relaxed max-w-2xl">
          Questions about our compounds, lab reports, or your order? Our team responds within 24 hours.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] tracking-[0.18em] uppercase text-gray-500 font-semibold">Name</Label>
              <Input name="name" required className="mt-2 rounded-none border-gray-300 h-11" data-testid="contact-name-input" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.18em] uppercase text-gray-500 font-semibold">Email</Label>
              <Input name="email" type="email" required className="mt-2 rounded-none border-gray-300 h-11" data-testid="contact-email-input" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-gray-500 font-semibold">Subject</Label>
            <Input name="subject" className="mt-2 rounded-none border-gray-300 h-11" data-testid="contact-subject-input" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-gray-500 font-semibold">Message</Label>
            <Textarea name="message" required rows={7} className="mt-2 rounded-none border-gray-300" data-testid="contact-message-input" />
          </div>
          <Button type="submit" disabled={submitting} className="btn-primary h-12 px-8 text-[12px] font-semibold uppercase tracking-[0.18em] rounded-none" data-testid="contact-submit-btn">
            <Send className="h-4 w-4 mr-2" /> {submitting ? "Sending…" : "Send Message"}
          </Button>
        </form>

        <aside className="space-y-6">
          <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c]">Direct Lines</div>
          {[
            { icon: Phone, t: "Phone", d: BRAND.phone },
            { icon: Mail, t: "Email", d: BRAND.email },
            { icon: MapPin, t: "Location", d: "Made & shipped from the USA" },
            { icon: Clock, t: "Hours", d: "Mon–Fri 9am–6pm EST" },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="flex items-start gap-4 pb-5 border-b border-gray-100">
              <Icon className="h-4 w-4 text-[#b91c1c] mt-0.5" />
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-gray-400 font-semibold">{t}</div>
                <div className="text-[14px] text-[#0a0a0a] font-medium mt-1">{d}</div>
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
};

export default Contact;
